export class PlanMetadataContractError extends Error {
    issues;
    constructor(issues) {
        super(`Plan metadata contract failed: ${issues.map((issue) => `${issue.path} ${issue.message}`).join("; ")}`);
        this.name = "PlanMetadataContractError";
        this.issues = issues;
    }
}
export const PLAN_BILLING_MODES = ["free", "self_serve", "sales_assisted", "manual"];
export const PLAN_CTA_KINDS = ["signup", "checkout", "sales", "disabled"];
export const PLAN_COMMERCIAL_METADATA_KEYS = [
    "buyerSegment",
    "supportTier",
    "sla",
    "securityReviewAssistance",
    "deploymentAssistance",
    "privateNetworking",
    "marketplaceTier",
    "observabilityTier",
    "rbacTier",
    "deploymentTypes",
    "complianceTier",
    "auditRetentionDays",
    "traceRetentionDays",
    "rateLimitTier",
    "analyticsTier",
];
export const PLAN_MODEL_ACCESS_METADATA_KEYS = [
    "platformProviderIds",
    "platformModelIds",
    "tenantProviderIds",
    "tenantModelIds",
];
export const PLAN_REQUIRED_VISIBLE_METADATA_KEYS = [
    "priceMonthlyCents",
    "billingMode",
    "cta",
    "changePolicy",
    "visibility",
    ...PLAN_COMMERCIAL_METADATA_KEYS,
];
export const PLAN_METADATA_CONTRACT_KEYS = [
    "sortOrder",
    "public",
    "highlighted",
    "badge",
    "theme",
    "priceMonthlyCents",
    "pricingLabel",
    "billingMode",
    "stripePriceId",
    "cta",
    "changePolicy",
    "visibility",
    ...PLAN_COMMERCIAL_METADATA_KEYS,
    "modelAccess",
    "usageBilling",
    "archivedAt",
    "archivedBy",
    "clonedFromPlanId",
    "source",
];
const BILLING_MODE_SET = new Set(PLAN_BILLING_MODES);
const CTA_KIND_SET = new Set(PLAN_CTA_KINDS);
const METADATA_KEY_SET = new Set(PLAN_METADATA_CONTRACT_KEYS);
const REQUIRED_VISIBLE_METADATA_KEY_SET = new Set(PLAN_REQUIRED_VISIBLE_METADATA_KEYS);
function readRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : undefined;
}
function readText(value) {
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}
function readTextArray(value) {
    if (!Array.isArray(value))
        return [];
    return value
        .map((entry) => readText(entry))
        .filter((entry) => entry !== undefined);
}
function readOptionalTextArray(value) {
    if (!Array.isArray(value))
        return undefined;
    return readTextArray(value);
}
function readBoolean(value, fallback) {
    return typeof value === "boolean" ? value : fallback;
}
function readNonNegativeNumber(value, fallback) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : fallback;
}
function readOptionalRetentionDays(value) {
    if (value === null)
        return null;
    return typeof value === "number" && Number.isFinite(value) && value >= 0
        ? Math.round(value)
        : undefined;
}
function readSortOrder(value, fallback) {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function defaultSortOrder(plan) {
    if (plan.planId === "free" || plan.tier === "free")
        return 10;
    if (plan.planId === "pro" || plan.tier === "pro")
        return 20;
    if (plan.planId === "team" || plan.tier === "team")
        return 30;
    if (plan.planId === "enterprise" || plan.tier === "enterprise")
        return 40;
    return 50;
}
function readBillingMode(plan, metadata, priceMonthlyCents) {
    const raw = readText(metadata["billingMode"]);
    if (raw && BILLING_MODE_SET.has(raw)) {
        return raw;
    }
    if (plan.planId === "free" || plan.tier === "free")
        return "free";
    if (readText(metadata["pricingLabel"]))
        return "sales_assisted";
    return priceMonthlyCents > 0 ? "self_serve" : "manual";
}
function defaultCtaLabel(billingMode) {
    switch (billingMode) {
        case "free":
            return "Start free";
        case "self_serve":
            return "Start free trial";
        case "sales_assisted":
            return "Talk to sales";
        case "manual":
            return "Contact administrator";
    }
}
function defaultCtaKind(billingMode) {
    switch (billingMode) {
        case "free":
            return "signup";
        case "self_serve":
            return "checkout";
        case "sales_assisted":
            return "sales";
        case "manual":
            return "disabled";
    }
}
function issue(path, message) {
    return { path, message };
}
function isNonNegativeIntegerOrNull(value) {
    return value === null || (typeof value === "number" && Number.isInteger(value) && value >= 0);
}
function isObjectRecord(value) {
    return readRecord(value) !== undefined;
}
function visibilityFromMetadata(metadata) {
    const visibility = readRecord(metadata["visibility"]);
    return {
        pricingPage: readBoolean(visibility?.["pricingPage"], true),
        tenantSettings: readBoolean(visibility?.["tenantSettings"], true),
        superadminOnly: readBoolean(visibility?.["superadminOnly"], false),
    };
}
export function planMetadataRequiresCommercialContract(plan) {
    if (plan.active === false)
        return false;
    const metadata = readRecord(plan.metadata) ?? {};
    const visibility = visibilityFromMetadata(metadata);
    if (visibility.superadminOnly)
        return false;
    const publicPlan = readBoolean(metadata["public"], true);
    return (publicPlan && visibility.pricingPage) || visibility.tenantSettings;
}
export function validatePlanMetadataContract(plan, options = {}) {
    const issues = [];
    const metadata = readRecord(plan.metadata);
    const requireCommercialMetadata = options.requireCommercialMetadata ?? planMetadataRequiresCommercialContract(plan);
    if (metadata === undefined) {
        issues.push(issue("metadata", "must be a JSON object"));
        return issues;
    }
    for (const key of Object.keys(metadata)) {
        if (!METADATA_KEY_SET.has(key)) {
            issues.push(issue(`metadata.${key}`, "is not a supported plan metadata key"));
        }
    }
    if (metadata["sortOrder"] !== undefined && !(typeof metadata["sortOrder"] === "number" && Number.isFinite(metadata["sortOrder"]))) {
        issues.push(issue("metadata.sortOrder", "must be a finite number"));
    }
    for (const key of ["public", "highlighted"]) {
        if (metadata[key] !== undefined && typeof metadata[key] !== "boolean") {
            issues.push(issue(`metadata.${key}`, "must be a boolean"));
        }
    }
    for (const key of ["badge", "theme", "pricingLabel", "stripePriceId", "archivedAt", "archivedBy", "clonedFromPlanId", "source"]) {
        if (metadata[key] !== undefined && readText(metadata[key]) === undefined) {
            issues.push(issue(`metadata.${key}`, "must be a non-empty string"));
        }
    }
    if (metadata["priceMonthlyCents"] !== undefined && !(typeof metadata["priceMonthlyCents"] === "number" && Number.isInteger(metadata["priceMonthlyCents"]) && metadata["priceMonthlyCents"] >= 0)) {
        issues.push(issue("metadata.priceMonthlyCents", "must be a non-negative integer"));
    }
    if (metadata["billingMode"] !== undefined && !(typeof metadata["billingMode"] === "string" && BILLING_MODE_SET.has(metadata["billingMode"]))) {
        issues.push(issue("metadata.billingMode", `must be one of ${PLAN_BILLING_MODES.join(", ")}`));
    }
    const cta = readRecord(metadata["cta"]);
    if (metadata["cta"] !== undefined && cta === undefined) {
        issues.push(issue("metadata.cta", "must be a JSON object"));
    }
    if (cta !== undefined) {
        if (readText(cta["label"]) === undefined) {
            issues.push(issue("metadata.cta.label", "must be a non-empty string"));
        }
        if (!(typeof cta["kind"] === "string" && CTA_KIND_SET.has(cta["kind"]))) {
            issues.push(issue("metadata.cta.kind", `must be one of ${PLAN_CTA_KINDS.join(", ")}`));
        }
        if (cta["href"] !== undefined && readText(cta["href"]) === undefined) {
            issues.push(issue("metadata.cta.href", "must be a non-empty string when provided"));
        }
    }
    const changePolicy = readRecord(metadata["changePolicy"]);
    if (metadata["changePolicy"] !== undefined && changePolicy === undefined) {
        issues.push(issue("metadata.changePolicy", "must be a JSON object"));
    }
    if (changePolicy !== undefined) {
        for (const key of ["allowSelfServeUpgrade", "allowSelfServeDowngrade", "requiresSales", "requiresPaymentMethod"]) {
            if (changePolicy[key] !== undefined && typeof changePolicy[key] !== "boolean") {
                issues.push(issue(`metadata.changePolicy.${key}`, "must be a boolean"));
            }
        }
    }
    const visibility = readRecord(metadata["visibility"]);
    if (metadata["visibility"] !== undefined && visibility === undefined) {
        issues.push(issue("metadata.visibility", "must be a JSON object"));
    }
    if (visibility !== undefined) {
        for (const key of ["pricingPage", "tenantSettings", "superadminOnly"]) {
            if (visibility[key] !== undefined && typeof visibility[key] !== "boolean") {
                issues.push(issue(`metadata.visibility.${key}`, "must be a boolean"));
            }
        }
    }
    for (const key of [
        "buyerSegment",
        "supportTier",
        "sla",
        "securityReviewAssistance",
        "deploymentAssistance",
        "privateNetworking",
        "marketplaceTier",
        "observabilityTier",
        "rbacTier",
        "complianceTier",
        "rateLimitTier",
        "analyticsTier",
    ]) {
        if (metadata[key] !== undefined && readText(metadata[key]) === undefined) {
            issues.push(issue(`metadata.${key}`, "must be a non-empty string"));
        }
    }
    if (metadata["deploymentTypes"] !== undefined) {
        const deploymentTypes = readTextArray(metadata["deploymentTypes"]);
        if (!Array.isArray(metadata["deploymentTypes"]) || deploymentTypes.length !== metadata["deploymentTypes"].length) {
            issues.push(issue("metadata.deploymentTypes", "must be an array of non-empty strings"));
        }
    }
    for (const key of ["auditRetentionDays", "traceRetentionDays"]) {
        if (metadata[key] !== undefined && !isNonNegativeIntegerOrNull(metadata[key])) {
            issues.push(issue(`metadata.${key}`, "must be a non-negative integer or null"));
        }
    }
    if (metadata["usageBilling"] !== undefined && !isObjectRecord(metadata["usageBilling"])) {
        issues.push(issue("metadata.usageBilling", "must be a JSON object"));
    }
    const modelAccess = readRecord(metadata["modelAccess"]);
    if (metadata["modelAccess"] !== undefined && modelAccess === undefined) {
        issues.push(issue("metadata.modelAccess", "must be a JSON object"));
    }
    if (modelAccess !== undefined) {
        const allowedModelAccessKeys = new Set(PLAN_MODEL_ACCESS_METADATA_KEYS);
        for (const key of Object.keys(modelAccess)) {
            if (!allowedModelAccessKeys.has(key)) {
                issues.push(issue(`metadata.modelAccess.${key}`, "is not a supported model access key"));
            }
        }
        for (const key of PLAN_MODEL_ACCESS_METADATA_KEYS) {
            if (modelAccess[key] !== undefined) {
                const values = readTextArray(modelAccess[key]);
                if (!Array.isArray(modelAccess[key]) || values.length !== modelAccess[key].length) {
                    issues.push(issue(`metadata.modelAccess.${key}`, "must be an array of non-empty strings"));
                }
            }
        }
    }
    if (requireCommercialMetadata) {
        for (const key of PLAN_REQUIRED_VISIBLE_METADATA_KEYS) {
            if (!Object.prototype.hasOwnProperty.call(metadata, key)) {
                issues.push(issue(`metadata.${key}`, "is required for active public or tenant-visible plans"));
            }
        }
        if (Array.isArray(metadata["deploymentTypes"]) && readTextArray(metadata["deploymentTypes"]).length === 0) {
            issues.push(issue("metadata.deploymentTypes", "must include at least one deployment type for visible plans"));
        }
        if (cta !== undefined && readText(cta["label"]) === undefined) {
            issues.push(issue("metadata.cta.label", "is required for visible plans"));
        }
        if (changePolicy !== undefined) {
            for (const key of ["allowSelfServeUpgrade", "allowSelfServeDowngrade", "requiresSales", "requiresPaymentMethod"]) {
                if (!Object.prototype.hasOwnProperty.call(changePolicy, key)) {
                    issues.push(issue(`metadata.changePolicy.${key}`, "is required for visible plans"));
                }
            }
        }
        if (visibility !== undefined) {
            for (const key of ["pricingPage", "tenantSettings", "superadminOnly"]) {
                if (!Object.prototype.hasOwnProperty.call(visibility, key)) {
                    issues.push(issue(`metadata.visibility.${key}`, "is required for visible plans"));
                }
            }
        }
    }
    for (const key of Object.keys(metadata)) {
        if (REQUIRED_VISIBLE_METADATA_KEY_SET.has(key) && metadata[key] === undefined) {
            issues.push(issue(`metadata.${key}`, "must not be undefined"));
        }
    }
    return issues;
}
export function assertPlanMetadataContract(plan, options = {}) {
    const issues = validatePlanMetadataContract(plan, options);
    if (issues.length > 0) {
        throw new PlanMetadataContractError(issues);
    }
}
export function parsePlanMetadata(plan) {
    const metadata = readRecord(plan.metadata) ?? {};
    const priceMonthlyCents = Math.round(readNonNegativeNumber(metadata["priceMonthlyCents"], 0));
    const billingMode = readBillingMode(plan, metadata, priceMonthlyCents);
    const cta = readRecord(metadata["cta"]);
    const changePolicy = readRecord(metadata["changePolicy"]);
    const visibility = readRecord(metadata["visibility"]);
    const pricingLabel = readText(metadata["pricingLabel"]);
    const stripePriceId = readText(metadata["stripePriceId"]);
    const badge = readText(metadata["badge"]);
    const ctaHref = readText(cta?.["href"]);
    const rawCtaKind = readText(cta?.["kind"]);
    const buyerSegment = readText(metadata["buyerSegment"]);
    const supportTier = readText(metadata["supportTier"]);
    const sla = readText(metadata["sla"]);
    const securityReviewAssistance = readText(metadata["securityReviewAssistance"]);
    const deploymentAssistance = readText(metadata["deploymentAssistance"]);
    const privateNetworking = readText(metadata["privateNetworking"]);
    const marketplaceTier = readText(metadata["marketplaceTier"]);
    const observabilityTier = readText(metadata["observabilityTier"]);
    const rbacTier = readText(metadata["rbacTier"]);
    const complianceTier = readText(metadata["complianceTier"]);
    const rateLimitTier = readText(metadata["rateLimitTier"]);
    const analyticsTier = readText(metadata["analyticsTier"]);
    const modelAccess = readRecord(metadata["modelAccess"]);
    const platformProviderIds = readOptionalTextArray(modelAccess?.["platformProviderIds"]);
    const platformModelIds = readOptionalTextArray(modelAccess?.["platformModelIds"]);
    const tenantProviderIds = readOptionalTextArray(modelAccess?.["tenantProviderIds"]);
    const tenantModelIds = readOptionalTextArray(modelAccess?.["tenantModelIds"]);
    const auditRetentionDays = readOptionalRetentionDays(metadata["auditRetentionDays"]);
    const traceRetentionDays = readOptionalRetentionDays(metadata["traceRetentionDays"]);
    const ctaKind = rawCtaKind && CTA_KIND_SET.has(rawCtaKind)
        ? rawCtaKind
        : defaultCtaKind(billingMode);
    return {
        sortOrder: readSortOrder(metadata["sortOrder"], defaultSortOrder(plan)),
        public: readBoolean(metadata["public"], true),
        highlighted: readBoolean(metadata["highlighted"], false),
        ...(badge !== undefined ? { badge } : {}),
        theme: readText(metadata["theme"]) ?? "gray",
        priceMonthlyCents,
        ...(pricingLabel !== undefined ? { pricingLabel } : {}),
        billingMode,
        ...(stripePriceId !== undefined ? { stripePriceId } : {}),
        cta: {
            label: readText(cta?.["label"]) ?? defaultCtaLabel(billingMode),
            kind: ctaKind,
            ...(ctaHref !== undefined ? { href: ctaHref } : {}),
        },
        changePolicy: {
            allowSelfServeUpgrade: readBoolean(changePolicy?.["allowSelfServeUpgrade"], billingMode === "free" || billingMode === "self_serve"),
            allowSelfServeDowngrade: readBoolean(changePolicy?.["allowSelfServeDowngrade"], billingMode === "free" || billingMode === "self_serve"),
            requiresSales: readBoolean(changePolicy?.["requiresSales"], billingMode === "sales_assisted"),
            requiresPaymentMethod: readBoolean(changePolicy?.["requiresPaymentMethod"], billingMode === "self_serve" && priceMonthlyCents > 0),
        },
        visibility: {
            pricingPage: readBoolean(visibility?.["pricingPage"], true),
            tenantSettings: readBoolean(visibility?.["tenantSettings"], true),
            superadminOnly: readBoolean(visibility?.["superadminOnly"], false),
        },
        commercial: {
            ...(buyerSegment !== undefined ? { buyerSegment } : {}),
            ...(supportTier !== undefined ? { supportTier } : {}),
            ...(sla !== undefined ? { sla } : {}),
            ...(securityReviewAssistance !== undefined ? { securityReviewAssistance } : {}),
            ...(deploymentAssistance !== undefined ? { deploymentAssistance } : {}),
            ...(privateNetworking !== undefined ? { privateNetworking } : {}),
            ...(marketplaceTier !== undefined ? { marketplaceTier } : {}),
            ...(observabilityTier !== undefined ? { observabilityTier } : {}),
            ...(rbacTier !== undefined ? { rbacTier } : {}),
            deploymentTypes: readTextArray(metadata["deploymentTypes"]),
            ...(complianceTier !== undefined ? { complianceTier } : {}),
            ...(auditRetentionDays !== undefined ? { auditRetentionDays } : {}),
            ...(traceRetentionDays !== undefined ? { traceRetentionDays } : {}),
            ...(rateLimitTier !== undefined ? { rateLimitTier } : {}),
            ...(analyticsTier !== undefined ? { analyticsTier } : {}),
        },
        ...(modelAccess !== undefined ? {
            modelAccess: {
                ...(platformProviderIds !== undefined ? { platformProviderIds } : {}),
                ...(platformModelIds !== undefined ? { platformModelIds } : {}),
                ...(tenantProviderIds !== undefined ? { tenantProviderIds } : {}),
                ...(tenantModelIds !== undefined ? { tenantModelIds } : {}),
            },
        } : {}),
        raw: metadata,
    };
}
//# sourceMappingURL=plan-metadata.js.map